import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  bodyErrors,
  bodyTargetErrors,
  errorSchema,
  guardErrors,
  idParams,
  targetErrors,
} from '../common';
import { recipeSchema } from '../entities';
import {
  RECIPE_INGREDIENTS_MAX,
  RECIPE_INGREDIENT_MAX,
  RECIPE_STEPS_MAX,
  RECIPE_STEP_MAX,
  nameInput,
  optionalMinutesInput,
  optionalServingsInput,
  recipeLinesInput,
  recipeNotesInput,
  recipeTagsInput,
} from '../inputs';

const c = initContract();

/**
 * The whole recipe, as create and edit both send it. Lines and tags arrive as
 * the form's rows and leave trimmed, blanks dropped, tags lower-cased.
 */
export const recipeBody = z.object({
  name: nameInput,
  servings: optionalServingsInput,
  minutes: optionalMinutesInput,
  ingredients: recipeLinesInput(RECIPE_INGREDIENTS_MAX, RECIPE_INGREDIENT_MAX),
  steps: recipeLinesInput(RECIPE_STEPS_MAX, RECIPE_STEP_MAX),
  tags: recipeTagsInput,
  notes: recipeNotesInput,
});

/** Parsed body — numbers as numbers, a blank number or notes as `null`. */
export type RecipeBody = z.infer<typeof recipeBody>;

/** Another of the user's recipes already has this name, whatever its case. */
const duplicateRecipe = { 409: errorSchema } as const;

export const recipesContract = c.router(
  {
    /** Every recipe, whole, A–Z — the browser searches them. */
    listRecipes: {
      method: 'GET',
      path: '/recipes',
      responses: { 200: z.array(recipeSchema), ...guardErrors },
    },

    getRecipe: {
      method: 'GET',
      path: '/recipes/:id',
      pathParams: idParams,
      responses: { 200: recipeSchema, ...targetErrors },
    },

    createRecipe: {
      method: 'POST',
      path: '/recipes',
      body: recipeBody,
      responses: { 201: recipeSchema, ...bodyErrors, ...duplicateRecipe },
    },

    /** The whole recipe: every list replaces what it had. */
    updateRecipe: {
      method: 'PUT',
      path: '/recipes/:id',
      pathParams: idParams,
      body: recipeBody,
      responses: { 200: recipeSchema, ...bodyTargetErrors, ...duplicateRecipe },
    },

    deleteRecipe: {
      method: 'DELETE',
      path: '/recipes/:id',
      pathParams: idParams,
      responses: { 204: c.noBody(), ...targetErrors },
    },
  },
  { pathPrefix: '/recipes' },
);
